import { DateTime } from "luxon";

import {
  ExperienceFe,
  MetricArrayType,
  VariationFe,
} from "@intelligems/ig-types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DashboardPageName } from "@src/api/types/dashboards";

import {
  getDefaultCompareGroup,
  getDefaultCompareGroupTb,
  getDefaultHiddenEndTime,
  getDefaultPersonalizationStartTime,
  getInitialControl,
} from "../helpers";
import type { IExperienceDashboardFilters } from "../types/dashboardFilters";
import { initialDashboardFiltersState } from "../types/dashboardFilters";

const slice = createSlice({
  name: "dashboardFilters",
  initialState: initialDashboardFiltersState,
  reducers: {
    clearDashboardFilters: (draft) => {
      Object.assign(draft, initialDashboardFiltersState);
    },
    setDashboardFilter: (
      draft,
      action: PayloadAction<IExperienceDashboardFilters>
    ) => {
      Object.assign(draft, action.payload);
    },
    refreshData: (draft) => {
      if (draft.endTime !== null) {
        draft.hiddenEndTime = draft.endTime as number;
      } else {
        draft.hiddenEndTime = DateTime.now().toMillis();
      }
      return draft;
    },
    setDashboardFilterByExperience: (
      draft,
      action: PayloadAction<{
        experience: ExperienceFe;
        variations: VariationFe[];
        page: DashboardPageName;
        dashboardData: MetricArrayType | undefined;
        shouldShowProfit: boolean;
        maxZScore: string;
        primaryMetricId: string;
        isTbAnalytics?: boolean;
        timezone?: string;
      }>
    ) => {
      const {
        experience,
        dashboardData,
        shouldShowProfit,
        maxZScore,
        page,
        variations,
        isTbAnalytics,
        primaryMetricId,
        timezone,
      } = action.payload;

      const controlGroup = variations.find((group) => group.isControl);

      if (controlGroup) {
        draft.isZScoreEnabled = true;
        draft.maxZScore = maxZScore;
        draft.testOrdersOnly =
          experience.experienceAnalysis.analyticsViewType === "none" ||
          experience.experienceAnalysis.analyticsViewType ===
            "test_orders_only";
        draft.startTime =
          experience.category === "personalization"
            ? getDefaultPersonalizationStartTime(experience, timezone)
            : null;
        draft.endTime =
          experience.category === "personalization" &&
          experience.status === "paused"
            ? getDefaultHiddenEndTime(experience)
            : null;
        draft.hiddenEndTime = getDefaultHiddenEndTime(experience);
        draft.experienceId = experience.id;
        draft.statSigView = "best";
        draft.visitorType = "any";
        draft.deviceType = "any";
        draft.customEvents = [];
        draft.countryCodes = [];
        draft.customEventsFilterType = "any";
        draft.reachedCheckoutStage = "any";
        draft.urlParam = {
          queryParam: null,
          queryParamFilterType: null,
          queryParamValue: null,
        };
        draft.landingPage = {
          landingPageUrlPath: null,
          landingPageUrlPathFilterType: null,
        };
        draft.userBehavior = {
          viewedCheckout: null,
          addedToCart: null,
          viewedProductPage: null,
          viewedCollectionPage: null,
        };

        draft.compareGroupA = getInitialControl(
          variations,
          page,
          isTbAnalytics === true
        );
        if (experience.category === "experiment") {
          if (isTbAnalytics) {
            draft.compareGroupB = getDefaultCompareGroupTb(
              variations,
              controlGroup,
              dashboardData as any[],
              primaryMetricId
            );
          } else {
            draft.compareGroupB = getDefaultCompareGroup(
              variations,
              controlGroup,
              page,
              dashboardData,
              shouldShowProfit
            );
          }
        }
      }
      return draft;
    },
    setDashboardFilterByPersonalization: (
      draft,
      action: PayloadAction<{
        experienceId: string;
        dashboardData: MetricArrayType | undefined;
        maxZScore: string;
      }>
    ) => {
      draft.isZScoreEnabled = true;
      draft.testOrdersOnly = false;
      draft.startTime = null;
      draft.endTime = null;
      draft.experienceId = action.payload.experienceId;
      draft.compareGroupA = undefined;
      draft.compareGroupB = undefined;
      draft.statSigView = "best";
      draft.deviceType = "any";
      draft.visitorType = "any";
      draft.maxZScore = action.payload.maxZScore;
      draft.customEvents = [];
      draft.countryCodes = [];
      draft.userBehavior = {
        viewedCheckout: null,
        addedToCart: null,
        viewedProductPage: null,
        viewedCollectionPage: null,
      };
      draft.urlParam = {
        queryParam: null,
        queryParamFilterType: null,
        queryParamValue: null,
      };
      draft.pageName = "overview";
      return draft;
    },
    setCompareGroups: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        page: DashboardPageName;
        dashboardData: MetricArrayType | undefined;
        shouldShowProfit: boolean;
        primaryMetricId: string;
        isTbAnalytics?: boolean;
      }>
    ) => {
      const controlGroup = action.payload.variations.find(
        (group) => group.isControl
      );
      if (controlGroup) {
        draft.compareGroupA = getInitialControl(
          action.payload.variations,
          action.payload.page,
          action.payload.isTbAnalytics === true
        );
        if (action.payload.isTbAnalytics) {
          draft.compareGroupB = getDefaultCompareGroupTb(
            action.payload.variations,
            controlGroup,
            action.payload.dashboardData as any[],
            action.payload.primaryMetricId
          );
        } else {
          draft.compareGroupB = getDefaultCompareGroup(
            action.payload.variations,
            controlGroup,
            action.payload.page,
            action.payload.dashboardData,
            action.payload.shouldShowProfit
          );
        }
      }
    },
    updateDashboardFilter: (
      draft,
      action: PayloadAction<{
        key: keyof IExperienceDashboardFilters;
        value: any;
      }>
    ) => {
      Object.assign(draft, {
        ...draft,
        [action.payload.key]: action.payload.value,
      });
    },
    setAnalyticsSubpage: (
      draft,
      action: PayloadAction<{
        key: string;
        value: DashboardPageName;
      }>
    ) => {
      draft[action.payload.key] = action.payload.value;
      const url = new URL(window.location.href);
      url.searchParams.set("subpage", action.payload.value);
      window.history.pushState(null, "", url.toString());
      return draft;
    },
  },
  selectors: {
    selectedDashboardFilters: (state) => state,
    selectedPageName: (state) => state.pageName,
  },
});

export const {
  clearDashboardFilters,
  updateDashboardFilter,
  setDashboardFilter,
  setDashboardFilterByExperience,
  setCompareGroups,
  setDashboardFilterByPersonalization,
  setAnalyticsSubpage,
  refreshData,
} = slice.actions;
export const { selectedDashboardFilters, selectedPageName } = slice.selectors;

export const dashboardFiltersReducer = slice.reducer;
